


interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string,
        type?: string,
        title?: string,
        description?: string,
        url?: string,
        site_name?: string,
        updated_time?: string,
        image?: string,
        image_secure_url?: string,
        image_width?: number,
        image_height?: number,
        image_alt?: string,
        image_type?: string,
        video?: string,
        video_duration?: number,
        video_upload_date?: string,
        video_allow_embed?: boolean,
    };
    twitter?: {
        card?: string,
        title?: string,
        description?: string,
        image?: string,
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface ingredients {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    // ingredients: ingredients;
    // treatmentAreas1: TreatmentArea[];
    // treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string,
        beforeAfter: string[] | any,
        introductionImage?: string,
    };
    product: {
        id: number,
        title: string,
        price: number,
        description: string,
    };
}

export const nadiv: {
    en: LanguageContent,
    ar: LanguageContent,
    shared: SharedData,
} =
{
    "en": {
        "url": "nad-iv-therapy",
        "meta": {
            "title": "NAD IV Therapy in Dubai | Elite Body Home",
            "description": "Boost cellular energy, combat aging, and enhance cognitive function with our NAD+ IV Therapy treatments designed to replenish this essential coenzyme.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/nad-iv-therapy/",
            "og": {
                "title": "NAD IV Therapy in Dubai | Elite Body Home",
                "description": "Replenish NAD+ levels to boost energy, mental clarity and cellular repair",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/09/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-50.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "NAD IV Therapy"],
        "hero": {
            "title": "NAD IV Therapy in Dubai",
            "subtitle": "Cellular Restoration Therapy",
            "description": "NAD IV Therapy is a novel wellness treatment aimed at boosting overall health and replenishing NAD+ levels in the body. NAD+ (Nicotinamide Adenine Dinucleotide) is a fundamental coenzyme involved in energy metabolism, DNA repair, and other cellular processes. As we age, NAD+ levels progressively decline, resulting in reduced cellular function and increased risk of chronic diseases. NAD+ IV Therapy provides a remedy by directly infusing this essential coenzyme into the bloodstream."
        },
        "howItWorks": {
            "title": "How Does NAD IV Therapy Work?",
            "description": "NAD IV Therapy works by delivering nicotinamide adenine dinucleotide directly into your bloodstream, bypassing the digestive system for maximum absorption. This allows your cells immediate access to this vital coenzyme that plays a key role in energy production and cellular repair."
        },
        "procedureSteps": {
            "heading": "Procedure Overview",
            "para": "Potential candidates might be more willing to try this treatment when they understand what the process entails. It is a non-invasive procedure that has done usually within a clinical setting under the medical supervision.",
            "steps": [
                {
                    "title": "Consultation",
                    "desc": "Patients have an initial consultation before starting therapy to discuss their health history, goals, and eligibility.",
                    "icon": "fa-solid fa-clipboard-question"
                },
                {
                    "title": "Preparation",
                    "desc": "The patient sits in a treatment chair, and the medical team maintains a clean and sterile environment. Vital signs, including blood pressure and heart rate, are monitored.",
                    "icon": "fa-solid fa-flask"
                },
                {
                    "title": "IV Insertion",
                    "desc": "For the IV, a small needle is placed into a vein, typically in the arm. An IV is attached to a drip bag that holds the NAD+ solution.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Administration of NAD+",
                    "desc": "The IV slowly administers the compound NAD+ solution. Depending on the dosage and tolerability, the duration of the therapy may vary from session to session.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "Monitoring",
                    "desc": "The medical team monitors the patient closely during the procedure to help ensure comfort and safety. Patients are usually told to relax, read, or look at their phones in the meantime.",
                    "icon": "fa-solid fa-user-md"
                },
                {
                    "title": "Completion",
                    "desc": "Once the NAD+ solution has been fully administered, the IV is removed and a small dressing is placed at the insertion site.",
                    "icon": "fa-solid fa-check-circle"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of NAD IV Therapy",
            "subheading": "Experience cellular rejuvenation and enhanced vitality",
            "para": "NAD IV Therapy offers comprehensive benefits for physical and mental wellbeing:",
            "items": [
                {
                    "title": "Increased Energy",
                    "desc": "It increases your energy levels and decreases fatigue",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Anti-Aging",
                    "desc": "Combating signs of aging and restores youthfulness",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "Brain Enhancement",
                    "desc": "Enhances brain activity, concentration, and memory",
                    "icon": "fa-solid fa-brain"
                },
                {
                    "title": "Metabolic Support",
                    "desc": "It helps optimize the metabolism and detoxification",
                    "icon": "fa-solid fa-fire"
                },
                {
                    "title": "Addiction Recovery",
                    "desc": "Promotes recovery from addiction and reduces cravings",
                    "icon": "fa-solid fa-hands-helping"
                },
                {
                    "title": "Immune Boost",
                    "desc": "Strengthens immune system",
                    "icon": "fa-solid fa-shield-alt"
                },
                {
                    "title": "Athletic Performance",
                    "desc": "Improves endurance and stamina for sports",
                    "icon": "fa-solid fa-running"
                },
                {
                    "title": "Cellular Repair",
                    "desc": "Heals lesions in DNA and stimulates cell regeneration",
                    "icon": "fa-solid fa-dna"
                },
                {
                    "title": "Mental Wellbeing",
                    "desc": "Enhances mental wellbeing, decreases anxiety and depression",
                    "icon": "fa-solid fa-smile"
                },
                {
                    "title": "Pain Relief",
                    "desc": "Alleviates chronic pain and inflammation",
                    "icon": "fa-solid fa-band-aid"
                },
                {
                    "title": "Weight Management",
                    "desc": "Aids in burning fat and losing weight",
                    "icon": "fa-solid fa-weight"
                }
            ]
        },
        "eligibility": {
            "heading": "Who Can Benefit from NAD IV Therapy?",
            "subheading": "",
            "para": "NAD IV Therapy is particularly beneficial for:",
            "items": [
                {
                    "title": "The Chronically Fatigued",
                    "desc": "NAD+ can help to restore energy levels and fight that state that keeps feeling tired.",
                    "icon": "fa-solid fa-tired"
                },
                {
                    "title": "Addiction Recovery",
                    "desc": "NAD IV Therapy helps with Withdrawal Symptoms and Cravings.",
                    "icon": "fa-solid fa-hands-helping"
                },
                {
                    "title": "Cognitively Impaired",
                    "desc": "NAD+ helps support brain health, which could benefit those with memory loss or neurodegenerative diseases.",
                    "icon": "fa-solid fa-brain"
                },
                {
                    "title": "Athletes",
                    "desc": "This treatment is anyone looking for performance enhancements in physical exercise and a much quicker recovery time for muscles.",
                    "icon": "fa-solid fa-running"
                },
                {
                    "title": "Aging Adults",
                    "desc": "Those looking to combat age-related decline in cellular function and energy levels.",
                    "icon": "fa-solid fa-user-clock"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation Before Your Treatment",
            "subheading": "",
            "para": "It is relatively easy to prepare for NAD IV Therapy, but there are certain steps that will ensure you get the most from the treatment:",
            "items": [
                {
                    "title": "Stay Hydrated",
                    "desc": "Hydrating well before your session makes the IV easier to place and helps with therapy absorption.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Eat a Light Meal",
                    "desc": "It's best to have a light meal before treatment to help prevent lightheadedness during the infusion.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "Wear Comfortable Clothing",
                    "desc": "This takes a few hours, so we recommend wearing loose, comfortable clothes.",
                    "icon": "fa-solid fa-tshirt"
                },
                {
                    "title": "Relax",
                    "desc": "It is important that you relax during the treatment, as NAD Drip is a low-stress procedure.",
                    "icon": "fa-solid fa-couch"
                }
            ]
        },
        "results": {
            "heading": "Expected Results",
            "subheading": "",
            "para": "NAD IV Therapy results are usually seen within a few sessions, with variation due to each person's health conditions and goals. What patients usually experience:",
            "items": [
                {
                    "title": "Energy Boost",
                    "desc": "Increased energy levels.",
                    "icon": "fa-solid fa-battery-full"
                },
                {
                    "title": "Mental Clarity",
                    "desc": "Enhanced mental clarity and focus.",
                    "icon": "fa-solid fa-lightbulb"
                },
                {
                    "title": "Physical Performance",
                    "desc": "Better physical performance.",
                    "icon": "fa-solid fa-dumbbell"
                },
                {
                    "title": "Reduced Fatigue",
                    "desc": "Decrease in fatigue and muscle soreness.",
                    "icon": "fa-solid fa-bed"
                },
                {
                    "title": "Emotional Balance",
                    "desc": "Improved mood and emotional stability.",
                    "icon": "fa-solid fa-smile-beam"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Aftercare Recommendations",
            "subheading": "",
            "para": "To maximize and prolong the benefits of your NAD IV Therapy:",
            "items": [
                {
                    "title": "Continue Hydrating",
                    "desc": "Drink plenty of water to help your body continue to process the NAD+ effectively.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Healthy Nutrition",
                    "desc": "Maintain a balanced diet rich in nutrients that support NAD+ production naturally.",
                    "icon": "fa-solid fa-apple-alt"
                },
                {
                    "title": "Follow-up Sessions",
                    "desc": "Consider a series of treatments for optimal results, as recommended by your healthcare provider.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Gentle Activity",
                    "desc": "Light exercise can help enhance circulation and the benefits of the therapy.",
                    "icon": "fa-solid fa-walking"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How long does a NAD IV Therapy session take?",
                    "answer": "The duration of NAD IV Therapy varies depending on the dosage and individual tolerability, typically ranging between 2-4 hours per session."
                },
                {
                    "question": "Is NAD IV Therapy painful?",
                    "answer": "Patients may feel a slight pinch during IV insertion, but the infusion itself is generally painless. Some people report mild warmth or tingling during the treatment."
                },
                {
                    "question": "How soon will I notice results from NAD IV Therapy?",
                    "answer": "Many patients report feeling increased energy and mental clarity immediately after treatment, with cumulative benefits becoming more noticeable after several sessions."
                },
                {
                    "question": "How often should I get NAD IV Therapy?",
                    "answer": "Frequency depends on individual health goals and conditions. For optimal results, an initial series of 5-10 sessions over 2-4 weeks is often recommended, followed by maintenance sessions."
                },
                {
                    "question": "Are there any side effects of NAD IV Therapy?",
                    "answer": "Side effects are typically mild and may include temporary flushing, nausea, or fatigue during the infusion. These usually subside quickly after the treatment."
                },
                {
                    "question": "Who should avoid NAD IV Therapy?",
                    "answer": "Individuals with certain medical conditions (like severe kidney disease) or those pregnant/nursing should consult their physician before treatment."
                },
                {
                    "question": "Can NAD IV Therapy help with addiction recovery?",
                    "answer": "Yes, NAD IV Therapy has shown promise in reducing withdrawal symptoms and cravings for individuals recovering from substance addiction."
                },
                {
                    "question": "What's the difference between NAD injections and IV therapy?",
                    "answer": "IV therapy delivers NAD+ directly into the bloodstream for 100% bioavailability, while injections have lower absorption rates and shorter-lasting effects."
                }
            ]
        }
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/New-Service-Bann61er-Image-8-scaled.jpg",
            "beforeAfter": []
        },
        "product": {
            "id": 7,
            "title": "NAD IV Therapy in Dubai",
            "description": "Advanced cellular restoration therapy to replenish NAD+ levels for enhanced energy, cognitive function and anti-aging benefits.",
            "price": 899,

        }
    },
    "ar": {
        "url": "nad-iv-therapy",
        "meta": {
            "title": "علاج NAD الوريدي في دبي | ايليت بودي هوم",
            "description": "علاج متخصص لتعزيز مستويات NAD+ في الجسم لتحسين الطاقة، الوضوح الذهني، وإصلاح الخلايا ومكافحة علامات الشيخوخة.",
            "robots": "index, follow",
            "canonical": "https://www.elitebodyhome.com/services/nad-iv-therapy",
            "og": {
                "title": "علاج NAD الوريدي في دبي | ايليت بودي هوم",
                "description": "علاج لتعزيز مستويات NAD+ لتحسين الطاقة والوظائف الخلوية",
                "image": "https://www.elitebodyhome.com/images/nad-iv-therapy-og.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "علاج NAD الوريدي"],
        "hero": {
            "title": "علاج NAD الوريدي في دبي",
            "subtitle": "علاج تجديد الخلايا",
            "description": "يعد علاج NAD الوريدي هو علاج حديث يهدف إلى تعزيز الصحة العامة وتجديد مستويات الـ NAD+ في الجسم. NAD+ (نيكوتيناميد أدينين ثنائي النوكليوتيد) هو إنزيم مساعد أساسي يشارك في عملية التمثيل الغذائي للطاقة، وإصلاح الحمض النووي، والعديد من العمليات الخلوية الأخرى. تتناقص مستويات الـ NAD+ تدريجيًا مع تقدمنا في العمر، مما يؤدي إلى اضطراب العمليات الخلوية وزيادة خطر الإصابة بالأمراض المزمنة. توفر جلسات علاج NAD+ الوريدي حلاً من خلال ضخ هذا الإنزيم المساعد الأساسي مباشرةً إلى مجرى الدم."
        },
        "howItWorks": {
            "title": "كيف يعمل علاج NAD الوريدي؟",
            "description": "يعمل علاج NAD الوريدي عن طريق إيصال النيكوتيناميد أدينين ثنائي النوكليوتيد مباشرة إلى مجرى الدم، متجاوزًا الجهاز الهضمي لضمان أقصى امتصاص. وهذا يمنح خلاياك وصولاً فوريًا إلى هذا الإنزيم المساعد الحيوي الذي يلعب دورًا رئيسيًا في إنتاج الطاقة وإصلاح الخلايا."
        },
        "procedureSteps": {
            "heading": "خطوات الإجراء",
            "para": "قد يكون المرشحون لهذا العلاج أكثر استعدادًا لتجربته عند فهمهم لخطوات الإجراء، حيث إنه إجراء غير جراحي يتم عادة في بيئة طبية تحت إشراف طبي.",
            "steps": [
                {
                    "title": "استشارة الطبيب",
                    "desc": "يخضع المرضى لاستشارة أولية قبل بدء العلاج لمناقشة تاريخهم الصحي، وأهدافهم، ومدى ملاءمتهم للعلاج.",
                    "icon": "fa-solid fa-clipboard-question"
                },
                {
                    "title": "الاستعداد",
                    "desc": "يجلس المريض في كرسي العلاج، ويحرص الفريق الطبي على الحفاظ على بيئة نظيفة ومعقمة، كما تُراقب العلامات الحيوية للمريض مثل ضغط الدم ومعدل ضربات القلب.",
                    "icon": "fa-solid fa-flask"
                },
                {
                    "title": "إدخال الإبرة الوريدية",
                    "desc": "يتم إدخال إبرة صغيرة في أحد الأوردة، عادة في وريد الذراع، ثم تُربط الإبرة الوريدية بقسطرة تحتوي على محلول NAD+.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "حقن محلول NAD+",
                    "desc": "يُعطى المحلول ببطء من خلال الـوريد. قد تختلف مدة الجلسة بناءً عن على الجرعة ومدى تحمل المريض للعلاج، كما قد تختلف المدة من جلسة إلى أخرى.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "المتابعة",
                    "desc": "يقوم الفريق الطبي بمراقبة المريض عن كثب أثناء الإجراء لضمان الراحة والسلامة. يُنصح المرضى عادةً بالاسترخاء أو القراءة أو التصفح في هواتفهم أثناء الجلسة.",
                    "icon": "fa-solid fa-user-md"
                },
                {
                    "title": "الانتهاء",
                    "desc": "بمجرد الانتهاء من إعطاء محلول الـ NAD+، يتم إزالة الـإبرة الوريدية ويتم وضع ضمادة صغيرة في موقع الحقن.",
                    "icon": "fa-solid fa-check-circle"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد العلاج",
            "subheading": "تجربة تجديد خلوي وحيوية محسنة",
            "para": "يقدم علاج NAD الوريدي فوائد شاملة للصحة الجسدية والعقلية:",
            "items": [
                {
                    "title": "زيادة الطاقة",
                    "desc": "يرفع مستويات الطاقة ويقلل من الإجهاد.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "مكافحة الشيخوخة",
                    "desc": "يقاوم علامات الشيخوخة ويساعد في استعادة الشباب.",
                    "icon": "fa-solid fa-heartbeat"
                },
                {
                    "title": "تعزيز نشاط الدماغ",
                    "desc": "تعزيز نشاط الدماغ والتمثيل الغذائي والتركيز والذاكرة.",
                    "icon": "fa-solid fa-brain"
                },
                {
                    "title": "دعم التمثيل الغذائي",
                    "desc": "يساعد في تحسين عملية التمثيل الغذائي وعمليات التخلص من السموم.",
                    "icon": "fa-solid fa-fire"
                },
                {
                    "title": "التعافي من الإدمان",
                    "desc": "يعزز التعافي من الإدمان ويقلل من الرغبة الشديدة المصاحبة له.",
                    "icon": "fa-solid fa-hands-helping"
                },
                {
                    "title": "تعزيز المناعة",
                    "desc": "يقوي جهاز المناعة.",
                    "icon": "fa-solid fa-shield-alt"
                },
                {
                    "title": "الأداء الرياضي",
                    "desc": "يحسن التحمل والقدرة البدنية للرياضة.",
                    "icon": "fa-solid fa-running"
                },
                {
                    "title": "إصلاح الخلايا",
                    "desc": "يعالج تلف الحمض النووي ويحفز تجديد الخلايا.",
                    "icon": "fa-solid fa-dna"
                },
                {
                    "title": "الصحة العقلية",
                    "desc": "يعزز صحة العقل ويقلل من القلق والاكتئاب.",
                    "icon": "fa-solid fa-smile"
                },
                {
                    "title": "تخفيف الآلام",
                    "desc": "يخفف من الألم المزمن والالتهابات.",
                    "icon": "fa-solid fa-band-aid"
                },
                {
                    "title": "إدارة الوزن",
                    "desc": "يساعد في حرق الدهون وفقدان الوزن.",
                    "icon": "fa-solid fa-weight"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية للعلاج",
            "subheading": "",
            "para": "يعد علاج NAD الوريدي مناسب لكلٍ من:",
            "items": [
                {
                    "title": "المصابون بالإرهاق المزمن",
                    "desc": "يمكن أن يساعد NAD+ في استعادة مستويات الطاقة ومحاربة الإرهاق المستمر والشعور بالتعب.",
                    "icon": "fa-solid fa-tired"
                },
                {
                    "title": "الأفراد في مرحلة التعافي من الإدمان",
                    "desc": "يساعد علاج NAD الوريدي في تخفيف أعراض الانسحاب والحد من الرغبة الشديدة المصاحبة لهذه المرحلة.",
                    "icon": "fa-solid fa-hands-helping"
                },
                {
                    "title": "الأشخاص الذين يعانون من ضعف الإدراك",
                    "desc": "يساعد NAD+ في دعم صحة الدماغ، مما يمكن أن يفيد الأشخاص الذين يعانون من فقدان الذاكرة أو الأمراض العصبية التنكسية.",
                    "icon": "fa-solid fa-brain"
                },
                {
                    "title": "الرياضيون",
                    "desc": "يعد هذا العلاج مثاليًا لكل من يبحث عن تحسين الأداء في التمارين البدنية وتسريع وقت تعافي العضلات.",
                    "icon": "fa-solid fa-running"
                },
                {
                    "title": "كبار السن",
                    "desc": "الذين يسعون لمكافحة التراجع المرتبط بالعمر في الوظائف الخلوية ومستويات الطاقة.",
                    "icon": "fa-solid fa-user-clock"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد قبل الجلسة",
            "subheading": "",
            "para": "يُعد من السهل نسبيًا التحضير لهذا العلاج، لكن هناك بعض الخطوات التي تضمن لك الاستفادة القصوى من الجلسة.",
            "items": [
                {
                    "title": "الحفاظ على الترطيب",
                    "desc": "يُفضل شرب كمية كافية من الماء قبل الجلسة، حيث يساعد ذلك في تسهيل وضع الإبرة الوريدية وتحسين امتصاص العلاج.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "تناول وجبة خفيفة",
                    "desc": "يفضل تناول وجبة خفيفة قبل العلاج لتجنب الشعور بالدوار أثناء الجلسة.",
                    "icon": "fa-solid fa-utensils"
                },
                {
                    "title": "ارتداء ملابس مريحة",
                    "desc": "نظرًا لأن الجلسة قد تستغرق بضع ساعات، يوصى بارتداء ملابس فضفاضة ومريحة.",
                    "icon": "fa-solid fa-tshirt"
                },
                {
                    "title": "الاسترخاء",
                    "desc": "يُنصح بالاسترخاء أثناء الجلسة، حيث أن علاج NAD+ الوريدي غير مؤلم.",
                    "icon": "fa-solid fa-couch"
                }
            ]
        },
        "results": {
            "heading": "النتائج المتوقعة",
            "subheading": "",
            "para": "عادةً ما تُلاحظ نتائج علاج NAD الوريدي بعد عدة جلسات، مع بعض التفاوت بناءً على حالة كل شخص وأهدافه. يلاحظ المرضى عادةً:",
            "items": [
                {
                    "title": "زيادة الطاقة",
                    "desc": "زيادة مستويات الطاقة.",
                    "icon": "fa-solid fa-battery-full"
                },
                {
                    "title": "وضوح ذهني",
                    "desc": "تعزيز الصفاء الذهني والتركيز.",
                    "icon": "fa-solid fa-lightbulb"
                },
                {
                    "title": "أداء بدني أفضل",
                    "desc": "تحسين الأداء البدني.",
                    "icon": "fa-solid fa-dumbbell"
                },
                {
                    "title": "تقليل الإرهاق",
                    "desc": "تقليل الإرهاق وألم العضلات.",
                    "icon": "fa-solid fa-bed"
                },
                {
                    "title": "توازن عاطفي",
                    "desc": "تحسين الحالة المزاجية والاستقرار العاطفي.",
                    "icon": "fa-solid fa-smile-beam"
                }
            ]
        },
        "aftercareTips": {
            "heading": "نصائح ما بعد العلاج",
            "subheading": "",
            "para": "لتعزيز وإطالة فوائد علاج NAD الوريدي:",
            "items": [
                {
                    "title": "الاستمرار في الترطيب",
                    "desc": "اشرب الكثير من الماء لمساعدة جسمك على الاستمرار في معالجة NAD+ بشكل فعال.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "التغذية الصحية",
                    "desc": "حافظ على نظام غذائي متوازن غني بالمغذيات التي تدعم إنتاج NAD+ بشكل طبيعي.",
                    "icon": "fa-solid fa-apple-alt"
                },
                {
                    "title": "جلسات المتابعة",
                    "desc": "فكر في سلسلة من العلاجات للحصول على أفضل النتائج، كما يوصي مقدم الرعاية الصحية الخاص بك.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "النشاط الخفيف",
                    "desc": "يمكن أن يساعد النشاط الخفيف في تعزيز الدورة الدموية وفوائد العلاج.",
                    "icon": "fa-solid fa-walking"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "كم تستغرق جلسة علاج NAD الوريدي؟",
                    "answer": "تختلف مدة الجلسة بناءً على الجرعة ومدى تحمل المريض، وعادةً ما تستغرق ما بين 2 إلى 4 ساعات لكل جلسة."
                },
                {
                    "question": "هل علاج NAD الوريدي مؤلم؟",
                    "answer": "قد يشعر المريض بوخز بسيط عند إدخال الإبرة، لكن التسريب نفسه غير مؤلم عمومًا. بعض المرضى يبلغون عن شعور طفيف بالدفء أو الوخز أثناء الجلسة."
                },
                {
                    "question": "متى سألاحظ نتائج علاج NAD الوريدي؟",
                    "answer": "يُبلغ العديد من المرضى عن شعورهم بزيادة الطاقة والوضوح الذهني مباشرةً بعد الجلسة، مع ظهور فوائد تراكمية أكثر وضوحًا بعد عدة جلسات."
                },
                {
                    "question": "كم مرة يجب أن أحصل على علاج NAD الوريدي؟",
                    "answer": "يعتمد التكرار على الأهداف الصحية الفردية. للحصول على أفضل النتائج، يُنصح عادةً بسلسلة أولية من 5-10 جلسات على مدى 2-4 أسابيع، تليها جلسات صيانة دورية."
                },
                {
                    "question": "هل هناك أي آثار جانبية لعلاج NAD الوريدي؟",
                    "answer": "الآثار الجانبية عادة ما تكون خفيفة وقد تشمل احمرارًا مؤقتًا أو غثيانًا أو إرهاقًا أثناء التسريب. هذه الأعراض تختفي عادةً بسرعة بعد انتهاء الجلسة."
                },
                {
                    "question": "من الذي يجب أن يتجنب علاج NAD الوريدي؟",
                    "answer": "يجب على الأشخاص الذين يعانون من حالات طبية معينة (مثل أمراض الكلى الشديدة) أو النساء الحوامل/المرضعات استشارة الطبيب قبل العلاج."
                },
                {
                    "question": "هل يمكن أن يساعد علاج NAD الوريدي في التعافي من الإدمان؟",
                    "answer": "نعم، أظهر علاج NAD الوريدي نتائج واعدة في تقليل أعراض الانسحاب والرغبة الشديدة لدى الأفراد الذين يتعافون من إدمان المواد."
                },
                {
                    "question": "ما الفرق بين حقن NAD والعلاج الوريدي؟",
                    "answer": "يوفر العلاج الوريدي NAD+ مباشرة في مجرى الدم مع توافر حيوي بنسبة 100%، بينما الحقن لها معدلات امتصاص أقل وتأثيرات أقصر مدة."
                }
            ]
        }
    }
}